import React, { useState } from 'react';
import { Loader2, Search, History } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { cn } from '../../lib/utils';
import { slidesBotApi, AdminUserRow } from '../../api/slidesBot';
import { PanelCard, useFetchOnce } from './index';
import { UserTransactionsDialog } from './UserTransactionsDialog';

interface Props {
  isDark: boolean;
}

const fmtUzs = (n: number) =>
  n > 0 ? n.toLocaleString('ru-RU').replace(/,/g, ' ') + ' so\'m' : '—';

const fmtDate = (s: string | null) => {
  if (!s) return '—';
  const d = new Date(s);
  return (
    d.toLocaleDateString('ru-RU') +
    ' ' +
    d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  );
};

export default function UsersView({ isDark }: Props) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [historyUser, setHistoryUser] = useState<AdminUserRow | null>(null);
  const limit = 20;

  const { data, loading, error } = useFetchOnce(
    () => slidesBotApi.listUsers({ page, limit, search }),
    [page, search],
  );

  const totalPages = data ? Math.ceil(data.total / limit) : 1;

  return (
    <div className="grid gap-4">
      <PanelCard isDark={isDark}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            setSearch(searchInput.trim());
          }}
          className="flex gap-2"
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by email, username, telegram..."
              className="pl-9"
            />
          </div>
          <Button type="submit">Search</Button>
        </form>
      </PanelCard>

      <PanelCard isDark={isDark} className="overflow-x-auto p-0">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[#0EA5A4]" />
          </div>
        ) : error ? (
          <p className="p-5 text-red-500">Failed to load: {error}</p>
        ) : !data || data.items.length === 0 ? (
          <p
            className={cn(
              'p-8 text-center',
              isDark ? 'text-slate-500' : 'text-slate-400',
            )}
          >
            No users yet
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Telegram</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead className="text-right">Used</TableHead>
                <TableHead className="text-right">Spent</TableHead>
                <TableHead>Last seen</TableHead>
                <TableHead className="w-[100px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>
                    <div className="font-medium">
                      {u.firstName ?? u.username}
                    </div>
                    <div className="text-xs text-slate-500">
                      {u.email ?? <span className="italic">no email</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    {u.telegramId ? (
                      <div>
                        <div className="text-sm">
                          {u.telegramUsername
                            ? `@${u.telegramUsername}`
                            : u.telegramId}
                        </div>
                        {u.telegramLinkedAt && (
                          <Badge variant="outline" className="mt-1 text-xs">
                            linked
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {u.decks_remaining}
                  </TableCell>
                  <TableCell className="text-right font-mono text-slate-500">
                    {u.decks_used_total}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {fmtUzs(u.total_spent_uzs)}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {fmtDate(u.last_seen)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setHistoryUser(u)}
                      title="View balance history"
                    >
                      <History className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </PanelCard>

      <UserTransactionsDialog
        isDark={isDark}
        isOpen={!!historyUser}
        onOpenChange={(open) => !open && setHistoryUser(null)}
        userId={historyUser?.id ?? null}
        userLabel={
          historyUser
            ? `${historyUser.firstName ?? historyUser.username}${
                historyUser.email ? ` (${historyUser.email})` : ''
              }`
            : ''
        }
      />

      {data && data.total > limit && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            {data.total} users · page {page} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              ← Prev
            </Button>
            <Button
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next →
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
